import { OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, ActivatedRoute, NavigationExtras, NavigationBehaviorOptions, Params, Route } from '@angular/router';

import { Subscription, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

// Cerium
import { CerFormUserViewType, CerMetaUserView, ICerViewPersistenceService } from '@form/cer-form-persistence.service';
import { CerRouteDataInfo } from '@app/app.module-generated';

// Cerium identity
import { CerAuthorizeService } from 'src/cer-identity/cer-authorize.service';

// Cerium application
import { CerSessionService } from 'src/cer/cer-session/cer-session.service';
import { CerUserRoute, CerSessionUserNavigationService } from '@cer/cer-session/cer-session-user-navigation.service';
import { urlAddressGet } from '../cer-app-documentation/cer-app-documentation-link-helper';

export class RouteCount {
  //public url: string;
  public nodeId: string;
  public count: number;
}

@Injectable({
  providedIn: 'root'
})
export class CerAppRouteService implements OnDestroy {

  private subscriptionManager$: Subscription = new Subscription();

  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public activeUserRoute$: BehaviorSubject<CerUserRoute> = new BehaviorSubject<CerUserRoute>(null);
  public activeView$: BehaviorSubject<CerMetaUserView> = new BehaviorSubject<CerMetaUserView>(null);
  public activeViewPersistenceService$: BehaviorSubject<ICerViewPersistenceService> = new BehaviorSubject<ICerViewPersistenceService>(null);
  public drawerClose$: Subject<boolean> = new Subject<boolean>();
  public routeCounts$: BehaviorSubject<RouteCount[]> = new BehaviorSubject<RouteCount[]>(null);
  public routeDataInfo$: BehaviorSubject<CerRouteDataInfo> = new BehaviorSubject<CerRouteDataInfo>(null);
  public currentUrl: string = '';

  private userNavigation: CerSessionUserNavigationService;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private browserTitleService: Title,
    private session: CerSessionService,
    private authService: CerAuthorizeService) {

    this.userNavigation = session.user.navigation;
    // Navigate start
    this.subscriptionManager$.add(
      this.router.events.pipe(
        // identify navigation start
        filter((event) => event instanceof NavigationStart),
        // now query the activated route
        map(() => CerAppRouteService.rootRoute(this.activatedRoute)),
        filter((route: ActivatedRoute) => route.outlet === 'primary')
      )
        .subscribe((route: ActivatedRoute) => {
          this.navigateStart();
        })
    );

    // Navigate end
    this.subscriptionManager$.add(
      this.router.events.pipe(
        // identify navigation end
        filter((event) => event instanceof NavigationEnd),
        // now query the activated route
        map(() => CerAppRouteService.rootRoute(this.activatedRoute)),
        filter((route: ActivatedRoute) => route.outlet === 'primary')
      )
        .subscribe((route: ActivatedRoute) => {
          //this.activeViewPersistenceService$.next(null); // Reset
          var url: string = route.snapshot.url.join('/');
          if (url?.length > 0) {
            const routeDataInfo: CerRouteDataInfo = route.routeConfig.data as CerRouteDataInfo;
            routeDataInfo.activatedRoute = route;
            routeDataInfo.url = url;
            this.currentUrl = url;
            this.routeDataInfo$.next(routeDataInfo);
          }
        })
    );

    /*
    this.subscriptionManager$.add(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        // now query the activated route
        map(() => CerAppRouteService.rootRoute(this.activatedRoute)),
        filter((route: ActivatedRoute) => route.outlet === 'primary')
      )
        .subscribe((route) => {
          if (route?.data *//*&& Array.isArray(route.data)*/ /*) {

route.data.forEach((data: any) => {
this.routeData$.next(data);
if (data.title?.length > 0) {
this.routeTitle$.next(data.title);
}
})
}
})
);
*/

    this.subscriptionManager$.add(
      combineLatest([this.session.environment.title$,
      this.routeDataInfo$]).subscribe(
        ([title, routeDataInfo]) => this.setBrowserTitle(title, routeDataInfo?.title))
    );

    this.subscriptionManager$.add(
      combineLatest([this.session.settings$, this.routeDataInfo$]).subscribe(([settings, routeDataInfo]) => {
        const url = routeDataInfo?.url;
        if (url?.length > 0 && !(url.startsWith('authentication'))) {
          this.userRouteViewSelect(this.userNavigation.userRouteGetOrDefault(url));
        }
      })
    );

    this.subscriptionManager$.add(
      this.session.sessionState$.subscribe(state =>
        this.sessionStateSubscribe(state))
    );
  }

  public ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  public setIsLoading(isLoading: boolean) {
    this.isLoading$.next(isLoading);
  }

  public routeGetByUrl(url: string): Route {
    const urlLwr = urlAddressGet(url)?.toLowerCase();
    return (urlLwr?.length > 0)
      ? this.router.config
        .find(r => r.path == '')?.children
        .find(r => r.path.toLocaleLowerCase() == urlLwr)
      : null;
  }

  private static rootRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  public navigateStart() {
    this.setIsLoading(true);
  }

  public reloadUrl() {
    const route = this.routeDataInfo$.getValue()?.activatedRoute?.snapshot;
    if (route) {
      const url = route.url.join('/');
      const urlExtras = { queryParams: route.queryParams } as NavigationBehaviorOptions;
      this.redirectToUrlInternal(url, true, urlExtras);
    }
    else {
      const url = this.currentUrl;
      if (url?.length > 0) {
        this.redirectToUrlInternal(url, true);
      }
    }
  }

  public reSignin() {
    var location: any = window.location;
    this.authService.navigateToLogin(location);
  }

  public reloadForcedUrl() {
    var location: any = window.location;
    location.reload(true);
  }

  public redirectTo(commands: any[], extras: NavigationExtras) {
    this.router.navigate(commands, extras);
  }

  public redirectToUrl(urlQueryString: string) {
    //const segments = urlQueryString?.split("?");
    //const url = segments[0];
    //var extras: NavigationBehaviorOptions = null;
    //if (segments.length > 1) {
    //  const queryParams = this.paramsFromString(segments[1]);
    //  if (queryParams != null) {
    //    extras = { queryParams: queryParams } as NavigationBehaviorOptions;
    //  }
    //}
    this.redirectToUrlInternal(urlQueryString, false /*url, true, extras*/);
  }

  /*
  private paramsFromString(queryString: string): Params {
    var params: Params = null;
    queryString.split('&').forEach(p => {
      const kv = p.split('=');
      params ??= {};
      params[kv[0]] = kv.length > 1 ? kv[1] : null;
    })
    return params;
  }
  */

  private redirectToUrlInternal(url: string, forceReload: boolean = true, extras: NavigationBehaviorOptions = null) {
    const urls = url?.split("?");
    if ((forceReload && url == this.currentUrl) /*|| (extras != null) */ || (urls?.length >= 2 && urls[0] == this.currentUrl)) {
      if (url == 'home' || url == '/') {
        this.router.navigate([url])
      }
      else {

        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.navigateByUrlFinal(url, extras);
        });
      }
      //this.router.navigateByUrl(url, { 
      //  onSameUrlNavigation: 'reload',
      //  skipLocationChange: true 
      //});
    }
    else if (url != this.currentUrl) {
      this.navigateByUrlFinal(url, extras);
    }
  }

  private navigateByUrlFinal(url: string, extras: NavigationBehaviorOptions = null) {
    if (extras != null) {
      extras.onSameUrlNavigation = 'reload';
      this.router.navigate([url], extras);
    }
    else {
      this.router.navigateByUrl(url);
    }
  }

  public setRouteTitle(title: string) {
    const routeDataInfo = Object.assign({}, this.routeDataInfo$.getValue());
    if (routeDataInfo != null) {
      routeDataInfo.title = title;
      this.routeDataInfo$.next(routeDataInfo);
    }
  }

  private setBrowserTitle(appTitle: string, routerTitle: string) {
    // Combine routerTitle and appTitle in browser tab caption
    var pageTitle = '';
    if (appTitle?.length > 0) {
      pageTitle = appTitle;
    }
    if (routerTitle?.length > 0) {
      pageTitle = pageTitle.length > 0 ? pageTitle + ' - ' + routerTitle : routerTitle;
    }
    this.browserTitleService.setTitle(pageTitle);
  }

  private sessionStateSubscribe(state: string): void {
    if (state == 'expired') {
      this.reSignin();
    }
  }

  public drawerClose(close: boolean) {
    this.drawerClose$.next(close);
  }

  public userRouteViewSelect(userRoute: CerUserRoute, view: CerMetaUserView = null) {
    if (userRoute != null) {
      this.activeUserRoute$.next(userRoute);
      view = view ?? this.userNavigation.viewGet(userRoute, userRoute.defaultView);
      this.userViewSelect(view);
    }
  }

  public userViewNameSelect(viewName: string) {
    var view = this.userNavigation.viewByNameGet(this.currentUrl, viewName);
    if (view) {
      this.userViewSelect(view);
    }
  }

  public userViewSelect(view: CerMetaUserView) {
    if (view) {
      this.activeView$.next(view);
    }
  }

  public userViewTypeSelect(userViewType: CerFormUserViewType) {
    var view = this.userNavigation.viewByTypeGet(this.currentUrl, userViewType);
    if (view) {
      this.userViewSelect(view);
    }
  }

  public userViewCustomSave(viewName: string, isPrimary: boolean, isDefault: boolean = null, viewNameOrig: string = null) {
    if (viewName?.length > 0) {
      var viewPersistenceData: string = this.userViewPersistenceGet();
      if (viewPersistenceData) {
        this.userNavigation.viewCustomSave(this.currentUrl, viewName, isPrimary, isDefault, viewPersistenceData, viewNameOrig);
        this.userRouteViewSelect(this.userNavigation.userRouteGetOrDefault(this.currentUrl));
      }
    }
  }

  private userViewPersistenceGet(): string {
    var persistenceView: string = null;
    var persistenceService: ICerViewPersistenceService = this.activeViewPersistenceService$.getValue();
    if (persistenceService) {
      persistenceView = persistenceService.getPersistenceView();
    }
    return persistenceView;
  }
}
